import React from 'react';
import { useNavigate } from 'react-router-dom';
import colors, { fonts, shadows } from '../theme';

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div style={{
      fontFamily: fonts.landing,
      background: colors.landing.bg,
      minHeight: '100vh',
      color: colors.landing.text,
    }}>
      <style>{`
        .gradient-text {
          background: ${colors.gradients.coral};
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

      {/* Header/Nav */}
      <nav style={{
        padding: '24px 48px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${colors.overlay.coralBorder}`,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          cursor: 'pointer',
        }}
        onClick={() => navigate('/')}>
          <img
            src="/logo192.png"
            alt="Miblio"
            style={{
              width: '40px',
              height: '40px',
            }}
          />
          <span style={{
            fontSize: '24px',
            fontWeight: 800,
          }} className="gradient-text">
            miblio
          </span>
        </div>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
          <button onClick={() => navigate('/login')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.text,
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 600,
          }}>
            Sign In
          </button>
          <button onClick={() => navigate('/register')} style={{
            background: colors.gradients.coral,
            border: 'none',
            color: colors.white,
            padding: '12px 32px',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 700,
            boxShadow: shadows.coralMd,
          }}>
            Get Started
          </button>
        </div>
      </nav>

      {/* Content */}
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '80px 48px',
      }}>
        <h1 style={{
          fontSize: '48px',
          fontWeight: 800,
          marginBottom: '16px',
          background: colors.gradients.coral,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}>
          Privacy Policy
        </h1>

        <p style={{
          fontSize: '14px',
          color: colors.gray[600],
          marginBottom: '48px',
        }}>
          Last Updated: December 29, 2024
        </p>

        <div style={{
          fontSize: '16px',
          lineHeight: 1.8,
          color: colors.landing.text,
        }}>
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '16px', color: colors.landing.coral }}>
              1. Introduction
            </h2>
            <p style={{ marginBottom: '16px' }}>
              Miblio ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our knowledge sharing and learning platform.
            </p>
            <p>
              By using Miblio, you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '16px', color: colors.landing.coral }}>
              2. Information We Collect
            </h2>

            <h3 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '12px', marginTop: '24px' }}>
              2.1 Information You Provide
            </h3>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px' }}>
              <li><strong>Account Information:</strong> Name, email address, password (encrypted)</li>
              <li><strong>Organization Information:</strong> Organization name, plan type</li>
              <li><strong>Content:</strong> Knowledge entries you create, quiz responses, feedback submissions</li>
              <li><strong>Integration Credentials:</strong> Encrypted API keys for Notion and other integrations you configure</li>
            </ul>

            <h3 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '12px', marginTop: '24px' }}>
              2.2 Information Collected Automatically
            </h3>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px' }}>
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent on platform</li>
              <li><strong>Device Information:</strong> IP address, browser type, operating system</li>
              <li><strong>Analytics:</strong> We use Vercel Analytics to understand how users interact with our platform</li>
            </ul>

            <h3 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '12px', marginTop: '24px' }}>
              2.3 Information from Third Parties
            </h3>
            <ul style={{ paddingLeft: '24px' }}>
              <li><strong>Notion:</strong> Metadata (titles, timestamps) from pages you choose to sync. We do NOT store your Notion content permanently.</li>
              <li><strong>Payment Processors:</strong> Stripe handles all payment information. We never see or store your credit card details.</li>
            </ul>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '16px', color: colors.landing.coral }}>
              3. How We Use Your Information
            </h2>
            <p style={{ marginBottom: '12px' }}>We use your information to:</p>
            <ul style={{ paddingLeft: '24px' }}>
              <li>Provide, maintain, and improve our services</li>
              <li>Create and manage your account</li>
              <li>Generate AI-powered quiz questions using OpenAI</li>
              <li>Sync content from your Notion workspace</li>
              <li>Send transactional emails (welcome, password reset, feedback confirmations)</li>
              <li>Respond to your feedback and support requests</li>
              <li>Monitor usage and detect technical issues</li>
              <li>Ensure platform security and prevent fraud</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '16px', color: colors.landing.coral }}>
              4. How We Share Your Information
            </h2>
            <p style={{ marginBottom: '12px' }}>We share your information only in these limited circumstances:</p>

            <h3 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '12px', marginTop: '24px' }}>
              4.1 Service Providers
            </h3>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px' }}>
              <li><strong>Supabase:</strong> Database hosting (US-based)</li>
              <li><strong>Railway:</strong> Backend infrastructure (US-based)</li>
              <li><strong>Vercel:</strong> Frontend hosting and analytics (US-based)</li>
              <li><strong>Resend:</strong> Transactional email delivery</li>
              <li><strong>OpenAI:</strong> AI quiz generation (we send content to generate questions)</li>
              <li><strong>Stripe:</strong> Payment processing (if applicable to your plan)</li>
            </ul>

            <h3 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '12px', marginTop: '24px' }}>
              4.2 Within Your Organization
            </h3>
            <p style={{ marginBottom: '16px' }}>
              Your knowledge entries, quiz scores, and activity are visible to other members of your organization, including administrators.
            </p>

            <h3 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '12px', marginTop: '24px' }}>
              4.3 Legal Requirements
            </h3>
            <p>
              We may disclose your information if required by law, court order, or to protect our rights or the safety of others.
            </p>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '16px', color: colors.landing.coral }}>
              5. Data Security
            </h2>
            <p style={{ marginBottom: '12px' }}>We implement industry-standard security measures:</p>
            <ul style={{ paddingLeft: '24px' }}>
              <li><strong>Encryption:</strong> All data encrypted at rest and in transit (TLS/SSL)</li>
              <li><strong>API Keys:</strong> Encrypted with AES-256-GCM before storage</li>
              <li><strong>Passwords:</strong> Hashed with bcrypt (never stored in plain text)</li>
              <li><strong>Access Controls:</strong> Role-based permissions (Admin, Manager, User)</li>
              <li><strong>Isolation:</strong> Complete data separation between organizations</li>
            </ul>
            <p style={{ marginTop: '16px' }}>
              However, no method of transmission over the internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.
            </p>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '16px', color: colors.landing.coral }}>
              6. Data Retention
            </h2>
            <ul style={{ paddingLeft: '24px' }}>
              <li><strong>Active Accounts:</strong> We retain your data as long as your account is active</li>
              <li><strong>Notion Content:</strong> We do NOT permanently store your Notion content. It's fetched live when needed.</li>
              <li><strong>AI-Generated Quizzes:</strong> Stored indefinitely unless you delete them</li>
              <li><strong>Deleted Accounts:</strong> Data deleted within 30 days of account deletion, except as required by law</li>
              <li><strong>Backups:</strong> May be retained for up to 90 days for disaster recovery</li>
            </ul>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '16px', color: colors.landing.coral }}>
              7. Your Privacy Rights
            </h2>
            <p style={{ marginBottom: '12px' }}>Depending on your location, you may have the following rights:</p>

            <h3 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '12px', marginTop: '24px' }}>
              7.1 All Users
            </h3>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px' }}>
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Export:</strong> Request a machine-readable copy of your data</li>
            </ul>

            <h3 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '12px', marginTop: '24px' }}>
              7.2 GDPR Rights (EU Users)
            </h3>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px' }}>
              <li><strong>Right to Object:</strong> Object to processing of your data</li>
              <li><strong>Right to Restrict:</strong> Request limited processing</li>
              <li><strong>Data Portability:</strong> Receive your data in a structured format</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
            </ul>

            <h3 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '12px', marginTop: '24px' }}>
              7.3 CCPA Rights (California Users)
            </h3>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px' }}>
              <li><strong>Know:</strong> What personal information we collect and how it's used</li>
              <li><strong>Delete:</strong> Request deletion of your personal information</li>
              <li><strong>Opt-Out:</strong> We do not sell personal information</li>
              <li><strong>Non-Discrimination:</strong> We won't discriminate against you for exercising your rights</li>
            </ul>

            <p style={{ marginTop: '16px' }}>
              To exercise any of these rights, contact us at <a href="mailto:privacy@miblio.io" style={{ color: colors.landing.coral, textDecoration: 'underline' }}>privacy@miblio.io</a>
            </p>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '16px', color: colors.landing.coral }}>
              8. Cookies and Tracking
            </h2>
            <p style={{ marginBottom: '12px' }}>We use the following types of cookies:</p>
            <ul style={{ paddingLeft: '24px' }}>
              <li><strong>Essential Cookies:</strong> Required for authentication and platform functionality</li>
              <li><strong>Analytics Cookies:</strong> Vercel Analytics to understand usage patterns (anonymized)</li>
            </ul>
            <p style={{ marginTop: '16px' }}>
              You can control cookies through your browser settings. Disabling essential cookies may affect platform functionality.
            </p>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '16px', color: colors.landing.coral }}>
              9. International Data Transfers
            </h2>
            <p>
              Our services are hosted in the United States. If you access Miblio from outside the US, your information will be transferred to, stored, and processed in the United States. By using our services, you consent to this transfer.
            </p>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '16px', color: colors.landing.coral }}>
              10. Children's Privacy
            </h2>
            <p>
              Miblio is not intended for children under 13. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '16px', color: colors.landing.coral }}>
              11. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes by email or through a notice on our platform. Continued use after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '16px', color: colors.landing.coral }}>
              12. Contact Us
            </h2>
            <p style={{ marginBottom: '12px' }}>
              If you have questions about this Privacy Policy or want to exercise your privacy rights:
            </p>
            <ul style={{ paddingLeft: '24px' }}>
              <li><strong>Email:</strong> <a href="mailto:privacy@miblio.io" style={{ color: colors.landing.coral, textDecoration: 'underline' }}>privacy@miblio.io</a></li>
              <li><strong>Security Issues:</strong> <a href="mailto:security@miblio.io" style={{ color: colors.landing.coral, textDecoration: 'underline' }}>security@miblio.io</a></li>
              <li><strong>General Support:</strong> <a href="mailto:support@miblio.io" style={{ color: colors.landing.coral, textDecoration: 'underline' }}>support@miblio.io</a></li>
            </ul>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        padding: '48px',
        textAlign: 'center',
        background: colors.landing.bg,
        borderTop: `1px solid ${colors.overlay.coralBorder}`,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          marginBottom: '16px',
        }}>
          <img
            src="/logo192.png"
            alt="Miblio"
            style={{
              width: '32px',
              height: '32px',
            }}
          />
          <span style={{
            fontSize: '24px',
            fontWeight: 800,
          }} className="gradient-text">
            miblio
          </span>
        </div>
        <div style={{
          display: 'flex',
          gap: '24px',
          justifyContent: 'center',
          marginBottom: '16px',
        }}>
          <button onClick={() => navigate('/')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.textLight,
            cursor: 'pointer',
            fontSize: '14px',
          }}>
            Home
          </button>
          <button onClick={() => navigate('/security')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.textLight,
            cursor: 'pointer',
            fontSize: '14px',
          }}>
            Security
          </button>
          <button onClick={() => navigate('/privacy')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.textLight,
            cursor: 'pointer',
            fontSize: '14px',
          }}>
            Privacy
          </button>
          <button onClick={() => navigate('/terms')} style={{
            background: 'transparent',
            border: 'none',
            color: colors.landing.textLight,
            cursor: 'pointer',
            fontSize: '14px',
          }}>
            Terms
          </button>
        </div>
        <p style={{
          fontSize: '14px',
          color: colors.landing.textLight,
        }}>
          © 2024 Miblio. Built for teams who learn together.
        </p>
      </footer>
    </div>
  );
}
